import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { showGettingStarted } from "@/components/GettingStartedCard";

const helpSections = [
  { id: "editor", title: "Working in the Editor", content: [
    "Click a segment to open it for editing.",
    "Accept a TM suggestion by clicking it, or type your own translation.",
    "Press Enter or click Confirm to mark the segment as translated.",
    "Green = 100% TM match. Yellow = fuzzy match, review before accepting.",
    "Tags are processed invisibly. Formatting is preserved on export.",
    "Use Ctrl+Up or Ctrl+Down to navigate between segments.",
  ]},
  { id: "tm", title: "Translation Memory", content: [
    "TM stores every confirmed segment automatically.",
    "Suggestions appear when a new segment matches a stored one.",
    "Import existing TM via Translation Memory page, Import TMX, up to 10MB.",
    "TMX is the standard format supported by all CAT tools.",
  ]},
  { id: "mt", title: "MT and Token Management", content: [
    "Three providers available: DeepL, Google Translate, MyMemory.",
    "Enable or disable providers in Settings.",
    "Use MyMemory for drafts to conserve DeepL and Google characters.",
    "Segments with 100% TM match do not need MT, confirm directly.",
    "Monitor usage in the USAGE section of the toolbar.",
    "DeepL recommended for Portuguese PT-PT projects.",
  ]},
  { id: "glossary", title: "Glossary", content: [
    "Glossary terms are highlighted inline as you translate.",
    "Add terms manually via Glossary in the sidebar.",
    "AI extracts key terms automatically when creating a project.",
    "Terms are shared across all projects with the same language pair.",
  ]},
  { id: "qa", title: "AI QA Review", content: [
    "Click Run QA in the REVIEW toolbar section.",
    "AI checks for consistency, untranslated content, and terminology issues.",
    "Results appear in a panel with direct navigation to each flagged segment.",
    "Premium: Batch MT with AI Review pre-screens all segments before you begin.",
  ]},
  { id: "export", title: "Exporting", content: [
    "Click Export in the toolbar when ready.",
    "DOCX Translation only produces clean formatted output.",
    "DOCX Bilingual shows source and target side by side.",
    "TMX exports Translation Memory for other tools.",
    "Confirm all segments before exporting, unconfirmed segments are excluded.",
    "Pre-Reform conversion applies automatically on export if enabled.",
  ]},
  { id: "versioning", title: "Document Versioning", content: [
    "Click New Version on a project card to upload a revised document.",
    "TranslyxCAT classifies each segment as confirmed, fuzzy, or new.",
    "Only modified and new segments require your attention.",
    "Available on Premium plan.",
  ]},
  { id: "aligner", title: "Auto-Aligner", content: [
    "Click Auto-Aligner in the Projects page toolbar.",
    "Upload source and translated documents.",
    "Review aligned pairs and click Import to TM.",
    "Available on Standard and Premium plans.",
  ]},
  { id: "prereform", title: "Portuguese Pre-Reform", content: [
    "Enable Pre-Reform in project settings for Angola and Mozambique clients.",
    "TranslyxCAT applies 80+ orthographic rules automatically on export.",
    "No manual correction required.",
    "Unique to TranslyxCAT, no other CAT tool offers this natively.",
  ]},
];

const HelpPage: React.FC = () => {
  const [search, setSearch] = useState("");
  const [openSection, setOpenSection] = useState<string | null>(null);
  const { user } = useAuth();
  const [restored, setRestored] = useState(false);

  const filtered = helpSections.filter(s =>
    search === "" ||
    s.title.toLowerCase().includes(search.toLowerCase()) ||
    s.content.some(c => c.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Help &amp; Guide</h1>
        <p className="text-gray-500 text-sm">Browse topics or search for a specific feature.</p>
      </div>
      {user && (
        <div className="mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => { showGettingStarted(user.id); setRestored(true); }}
            className="text-sm font-semibold text-gold-800 border border-gold-500/60 rounded-lg px-3 py-2 hover:bg-gold-50 transition-colors"
          >
            Show getting started
          </button>
          {restored && <span className="text-sm text-gray-700" role="status">Done. It will appear on your dashboard.</span>}
        </div>
      )}
      <input
        type="text"
        placeholder="Search help topics..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm mb-6 focus:outline-none focus:ring-2 focus:ring-gold-500"
      />
      <div className="space-y-2">
        {filtered.map(section => (
          <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
              className="w-full flex items-center justify-between px-4 py-3.5 text-left bg-white hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm font-semibold text-gray-900">{section.title}</span>
              <svg
                className={"w-4 h-4 text-gray-400 transition-transform " + (openSection === section.id ? "rotate-180" : "")}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openSection === section.id && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-2">
                {section.content
                  .filter(c => search === "" || c.toLowerCase().includes(search.toLowerCase()))
                  .map((tip, i) => (
                    <div key={i} className="flex gap-2 text-sm text-gray-600 leading-relaxed">
                      <span className="text-gold-500 mt-0.5 shrink-0">•</span>
                      <span>{tip}</span>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-8 p-4 bg-slate-50 border border-slate-200 rounded-xl text-center">
        <p className="text-sm text-slate-600 mb-2">Need the full step-by-step walkthrough?</p>
        <a href="https://translyx.app/guide/" target="_blank" rel="noreferrer" className="inline-block bg-gold-500 hover:bg-gold-400 text-slate-900 font-semibold text-sm px-5 py-2 rounded-lg transition-colors">Open Full Guide</a>
      </div>
    </div>
  );
};

export default HelpPage;
